import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import styled, { theme } from 'connect-dot-com-ui/components/Theme';
import { KOALA, LORAX, MARIGOLD } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITabs from 'UIComponents/nav/UITabs';
import UITab from 'UIComponents/nav/UITab';
import { useIsCurrentUser, useUserByUsername } from '../features/users/userQueries';
import { useParams } from 'react-router-dom';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIFlex from 'UIComponents/layout/UIFlex';
import { unwrapHubHttpError } from '../utils/api';
import { NavMarker } from 'react-rhumb';
import ErrorMessage from '../components/ErrorMessage';
import { PrerenderReady, PrerenderStatusCode } from '../components/Prerender';
import AcademyCredentials from '../features/credentials/components/AcademyCredentials';
import CommunityCredentials from '../features/credentials/components/CommunityCredentials';
import UserPrerender from '../features/users/components/UserPrerender';
import HeroCard from '../features/users/components/HeroCard';
import UserGoal from '../features/users/components/UserGoal';
import UserSocials from '../features/users/components/UserSocials';
import UserProducts from '../features/users/components/UserProducts';
const DecorativeHeader = styled(({
  className
}) => /*#__PURE__*/_jsx("div", {
  className: className,
  children: /*#__PURE__*/_jsxs("div", {
    className: "blob-parent",
    children: [/*#__PURE__*/_jsx("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      width: "280",
      height: "250",
      viewBox: "0 0 194 190",
      children: /*#__PURE__*/_jsx("path", {
        fill: KOALA,
        d: "M165.74,59.37a14.75,14.75,0,0,0,2.89,5.77c5.38,6.75,15.88,10.21,21.31,17.52,8.14,11,2.79,25.61-5.11,35.75-9.33,12.43-19.09,20-28.6,31.33-1.38,1.63-3.64,4.84-5.42,7.27-14.45,19.72-35.32,34.58-60.39,32.85-23.93-2-41.91-17.49-55.31-36.22-1.49-2.08-3.95-7-5.19-9.24C20.3,126.93,5.65,117.59,1.07,100.85A31.31,31.31,0,0,1,0,93.52c0-14,11.6-48,24.36-68q2.13-3.33,4.46-6.32C44.44-.51,71.18-1.14,93.6,4.24c2.5.6,4.94,1.28,7.34,2C108.44,8.66,112.85.07,118.87,0c14-.16,19.25,18.89,33.31,21.75,12.06,2.88,16.7,14.39,14.17,24.66-.13,1.69-.31,3.5-.53,5.38A16.75,16.75,0,0,0,165.74,59.37Z",
        fillRule: "evenodd"
      })
    }), /*#__PURE__*/_jsx("svg", {
      height: "56",
      width: "56",
      children: /*#__PURE__*/_jsx("circle", {
        cx: "28",
        cy: "28",
        r: "28",
        fill: LORAX
      })
    }), /*#__PURE__*/_jsx("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      width: "268",
      height: "208",
      viewBox: "0 0 238 188",
      children: /*#__PURE__*/_jsx("path", {
        fill: KOALA,
        d: "M72.29,146.62C19.59,116.29-11.1,70.93,3.74,45.3S49.6-19.27,102.3,11.06,270.91-10.41,229.08,113.14,125,177,72.29,146.62Z",
        fillRule: "evenodd"
      })
    }), /*#__PURE__*/_jsx("svg", {
      height: "56",
      width: "56",
      children: /*#__PURE__*/_jsx("circle", {
        cx: "27",
        cy: "27",
        r: "18",
        fill: MARIGOLD
      })
    })]
  })
})).withConfig({
  displayName: "Profile__DecorativeHeader",
  componentId: "sc-1i0pptb-0"
})(["background-color:", ";position:absolute;margin-top:-40px;left:0;right:0;height:140px;overflow:hidden;@media (max-width:", "){margin-top:-12px;}.blob-parent{position:relative;max-width:1280px;width:100%;height:100%;margin:auto;}svg{position:absolute;&:nth-child(1){top:-10px;left:5px;}&:nth-child(2){top:40px;left:5px;}&:nth-child(3){top:-80px;left:100%;}&:nth-child(4){top:60px;left:105%;}}"], props => props.theme.colors.GYPSUM, props => props.theme.sizes.GRID_BREAKPOINT_SMALL);
const HeroGrid = styled.div.withConfig({
  displayName: "Profile__HeroGrid",
  componentId: "sc-1i0pptb-1"
})(["display:grid;column-gap:24px;row-gap:24px;margin-bottom:40px;padding-top:16px;@media (min-width:", "){grid-template-columns:2fr 1fr;}"], props => props.theme.sizes.GRID_BREAKPOINT_MEDIUM);
const DetailsGrid = styled.div.withConfig({
  displayName: "Profile__DetailsGrid",
  componentId: "sc-1i0pptb-2"
})(["display:grid;column-gap:24px;row-gap:24px;margin-bottom:40px;padding-top:16px;&:empty{display:none;}@media (min-width:", "){", "}"], props => props.theme.sizes.GRID_BREAKPOINT_SMALL, props => props.$hasBoth && 'grid-template-columns: 1fr 2fr;');
const Tabs = styled(UITabs).withConfig({
  displayName: "Profile__Tabs",
  componentId: "sc-1i0pptb-3"
})(["margin-bottom:40px;[role='tabpanel']{padding:24px 0;}"]);

const Profile = () => {
  const {
    username
  } = useParams();
  const {
    user,
    userLoading,
    userError
  } = useUserByUsername(username);
  const ownProfile = useIsCurrentUser(user ? user.userId : undefined);

  if (userLoading) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      layout: "centered",
      minHeight: 100,
      size: "medium"
    });
  }

  const httpUserError = unwrapHubHttpError(userError);

  if (httpUserError && httpUserError.status === 404) {
    return /*#__PURE__*/_jsxs(NavMarker, {
      name: "PROFILE_LOAD_FAILURE",
      children: [/*#__PURE__*/_jsx(PrerenderStatusCode, {
        status: "404"
      }), /*#__PURE__*/_jsx(PrerenderReady, {}), /*#__PURE__*/_jsx(ErrorMessage, {
        type: "notFound"
      })]
    });
  }

  if (httpUserError && httpUserError.status === 403 || !ownProfile && user && !user.public) {
    return /*#__PURE__*/_jsxs(NavMarker, {
      name: "PRIVATE_PROFILE_LOADED",
      children: [/*#__PURE__*/_jsx(PrerenderStatusCode, {
        status: "403"
      }), /*#__PURE__*/_jsx(PrerenderReady, {}), /*#__PURE__*/_jsx(UIFlex, {
        justify: "center",
        children: /*#__PURE__*/_jsxs("div", {
          style: {
            maxWidth: 350,
            position: 'relative'
          },
          children: [/*#__PURE__*/_jsx(ErrorMessage, {
            type: "private"
          }), /*#__PURE__*/_jsx(UIIcon, {
            name: "locked",
            size: 50,
            color: theme.colors.EERIE,
            style: {
              position: 'absolute',
              right: 'calc(50% - 115px)',
              top: 45
            }
          })]
        })
      })]
    });
  }

  if (!user) {
    return /*#__PURE__*/_jsxs(NavMarker, {
      name: "PROFILE_LOAD_FAILURE",
      children: [/*#__PURE__*/_jsx(PrerenderStatusCode, {
        status: "500"
      }), /*#__PURE__*/_jsx(PrerenderReady, {}), /*#__PURE__*/_jsx(ErrorMessage, {
        type: "badRequest"
      })]
    });
  }

  const userHasSocials = Boolean(user.linkedinId || user.twitterId || user.githubId || user.meetingsId);
  const userHasProducts = !!user.products.length;
  return /*#__PURE__*/_jsxs("article", {
    "data-test-id": "profile-page",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "PROFILE_LOADED"
    }), /*#__PURE__*/_jsx(UserPrerender, {
      username: username
    }), /*#__PURE__*/_jsx(DecorativeHeader, {}), /*#__PURE__*/_jsxs(HeroGrid, {
      children: [/*#__PURE__*/_jsx(HeroCard, {
        username: username
      }), /*#__PURE__*/_jsx(UserGoal, {
        username: username
      })]
    }), /*#__PURE__*/_jsxs(DetailsGrid, {
      $hasBoth: userHasSocials && userHasProducts,
      children: [/*#__PURE__*/_jsx(UserSocials, {
        username: username
      }), /*#__PURE__*/_jsx(UserProducts, {
        username: username
      })]
    }), /*#__PURE__*/_jsxs(Tabs, {
      defaultSelected: "1",
      children: [/*#__PURE__*/_jsx(UITab, {
        tabId: "1",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "connectDotCom.academy"
        }),
        "data-test-id": "academy-tab",
        children: /*#__PURE__*/_jsx(AcademyCredentials, {
          username: username
        })
      }), /*#__PURE__*/_jsx(UITab, {
        tabId: "2",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "connectDotCom.community"
        }),
        "data-test-id": "community-tab",
        children: /*#__PURE__*/_jsx(CommunityCredentials, {
          username: username
        })
      })]
    })]
  });
};

export default Profile;